"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import { parseAbundances } from "@/lib/tool-kit/ecology/abundance";
import {
  type LogBase,
  type ShannonResult,
  shannon,
} from "@/lib/tool-kit/ecology/shannon";

/** English strings. A translated content file overrides these through `ui`. */
export const SHANNON_DIVERSITY_INDEX_CALCULATOR_STRINGS = {
  dataLabel:
    "Species counts — one species per line, name then count (or just the numbers)",
  dataHint:
    "For example “Oak 25”, one per line. Zero counts are ignored. Counts can be abundances, biomass or cover; whole numbers also give the bias-corrected value and standard error.",
  exampleData: "Oak 25\nBeech 18\nBirch 12\nHazel 8\nRowan 5\nHolly 2",
  unnamedSpecies: "Species {n}",
  baseLabel: "Logarithm base",
  baseE: "Natural log, ln (nats)",
  base2: "Base 2 (bits)",
  base10: "Base 10 (hartleys)",
  results: "Results",
  richness: "Species (S)",
  total: "Individuals (N)",
  entropy: "Shannon index H′ ({unit})",
  maxEntropy: "Maximum H′max = log S ({unit})",
  evenness: "Pielou's evenness J′ = H′ ÷ H′max",
  effective: "Effective number of species, exp(H′)",
  effectiveEvenness: "Effective number ÷ S",
  corrected: "Miller–Madow corrected H′ ({unit})",
  standardError: "Standard error of H′ ({unit})",
  unitE: "nats",
  unit2: "bits",
  unit10: "hartleys",
  undefined: "undefined",
  speciesHeading: "Species",
  columnSpecies: "Species",
  columnCount: "n",
  columnShare: "p = n ÷ N",
  columnLog: "log p",
  columnTerm: "−p × log p",
  workingHeading: "The working",
  workingEntropy: "H′ = −Σ p × log p = {sum} {unit}",
  workingEvenness: "J′ = H′ ÷ log S = {h} ÷ {max} = {result}",
  workingEffective: "exp(H′) = {result}",
  singleSpecies:
    "One species only: H′ = 0 and evenness is undefined, since log 1 = 0.",
  zerosIgnored:
    "one: {count} species with a zero count was ignored.|other: {count} species with zero counts were ignored.",
  unreadable: "These lines had no count and were skipped: {lines}",
  negative: "Counts cannot be negative.",
  copyResult: "Copy results",
  copied: "Copied!",
  copySummary:
    "S = {s}, N = {n}; H′ = {h} {unit}, J′ = {j}, exp(H′) = {effective}",
  enter: "Enter at least one species with a count above zero.",
} as const;

type Strings = typeof SHANNON_DIVERSITY_INDEX_CALCULATOR_STRINGS;

const ID = "sh";

function unitName(base: LogBase, strings: Strings): string {
  if (base === "2") {
    return strings.unit2;
  }
  return base === "10" ? strings.unit10 : strings.unitE;
}

export default function ShannonDiversityIndexCalculatorWidget() {
  const strings = useToolUi(SHANNON_DIVERSITY_INDEX_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [base, setBase] = useState<LogBase>("e");
  const parsed = parseAbundances(raw, (n) =>
    t(strings.unnamedSpecies, { n: fmt(n) })
  );
  const result = shannon(parsed.entries, base);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id={`${ID}-data`}
        label={strings.dataLabel}
        onChange={setRaw}
        prose
        rows={8}
        value={raw}
      />
      <PillGroup
        label={strings.baseLabel}
        name={`${ID}-base`}
        onChange={setBase}
        options={[
          { value: "e", label: strings.baseE },
          { value: "2", label: strings.base2 },
          { value: "10", label: strings.base10 },
        ]}
        value={base}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {parsed.negative ? <ErrorNotice>{strings.negative}</ErrorNotice> : null}
        {parsed.unreadable.length > 0 ? (
          <Notice>
            {t(strings.unreadable, { lines: parsed.unreadable.join("; ") })}
          </Notice>
        ) : null}
        {parsed.zeros > 0 ? (
          <Notice>
            {tPlural(strings.zerosIgnored, locale, parsed.zeros, {
              count: fmt(parsed.zeros),
            })}
          </Notice>
        ) : null}
        {result ? (
          <Results fmt={fmt} result={result} strings={strings} />
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </div>
  );
}

function Results({
  result,
  strings,
  fmt,
}: {
  result: ShannonResult;
  strings: Strings;
  fmt: NumberFormat;
}) {
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const maybe = (value: number | null) =>
    value === null ? strings.undefined : four(value);
  const unit = unitName(result.base, strings);
  const speciesRows = result.terms.map((term) => [
    term.label,
    fmt(term.count),
    four(term.share),
    four(term.logShare),
    four(term.term),
  ]);
  const copy = t(strings.copySummary, {
    s: fmt(result.richness),
    n: fmt(result.total),
    h: four(result.entropy),
    unit,
    j: maybe(result.evenness),
    effective: four(result.effective),
  });
  return (
    <div className="space-y-6">
      <StatGrid>
        <StatCard label={strings.richness} value={fmt(result.richness)} />
        <StatCard label={strings.total} value={fmt(result.total)} />
        <StatCard
          emphasis
          label={t(strings.entropy, { unit })}
          value={four(result.entropy)}
        />
        <StatCard
          emphasis
          label={strings.evenness}
          value={maybe(result.evenness)}
        />
        <StatCard label={strings.effective} value={four(result.effective)} />
        <StatCard
          label={t(strings.maxEntropy, { unit })}
          value={four(result.maxEntropy)}
        />
        <StatCard
          label={strings.effectiveEvenness}
          value={four(result.effectiveEvenness)}
        />
        {result.corrected === null ? null : (
          <StatCard
            label={t(strings.corrected, { unit })}
            value={four(result.corrected)}
          />
        )}
        {result.standardError === null ? null : (
          <StatCard
            label={t(strings.standardError, { unit })}
            value={four(result.standardError)}
          />
        )}
      </StatGrid>
      {result.richness === 1 ? <Notice>{strings.singleSpecies}</Notice> : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.speciesHeading}</h3>
        <DataTable
          head={[
            strings.columnSpecies,
            strings.columnCount,
            strings.columnShare,
            strings.columnLog,
            strings.columnTerm,
          ]}
          rows={speciesRows}
        />
      </div>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-1 font-mono text-foreground text-sm">
          <li>
            {t(strings.workingEntropy, {
              sum: four(result.entropy),
              unit,
            })}
          </li>
          {result.evenness === null ? null : (
            <li>
              {t(strings.workingEvenness, {
                h: four(result.entropy),
                max: four(result.maxEntropy),
                result: four(result.evenness),
              })}
            </li>
          )}
          <li>
            {t(strings.workingEffective, { result: four(result.effective) })}
          </li>
        </ul>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={copy}
      />
    </div>
  );
}
